<template>
  <div class="oln" :class="[$theme.darkTheme ? 'oln--dark' : 'oln--light', { 'oln--removed': isRemoved }]">
    <div class="oln__icon-wrap">
      <div class="oln__icon-bg">
        <i class="material-icons oln__icon">{{ icon }}</i>
      </div>
    </div>

    <div class="oln__body">
      <h3 class="oln__title">{{ title }}</h3>
      <p v-if="courseName" class="oln__course-name">{{ courseName }}</p>
      <p class="oln__desc">
        <template v-if="isMixed">{{ headline }}</template>
        <template v-else>O produtor {{ verb }} <strong>{{ count }}</strong> {{ count === 1 ? "aula" : "aulas" }} deste curso.</template>
        <br />
        {{ removalLine }}
      </p>
      <p v-if="names.length" class="oln__lessons">{{ names.join(", ") }}</p>
      <p v-if="canDownload" class="oln__question">Quer baixar a versão atualizada agora?</p>
    </div>

    <div class="oln__actions" :class="{ 'oln__actions--single': !canDownload }">
      <button v-if="canDownload" class="oln__btn oln__btn--ghost" @click="dismiss">Agora não</button>
      <button v-if="canDownload" class="oln__btn oln__btn--download" @click="redownload">Baixar atualização</button>
      <button v-else class="oln__btn oln__btn--download" @click="dismiss">Entendi</button>
    </div>
  </div>
</template>

<script>
export default {
  name: "OutdatedLessonsNotice",
  props: {
    courseName: { type: String, default: "" },
    names: { type: Array, default: () => [] },
    count: { type: Number, default: 0 },
    canDownload: { type: Boolean, default: false },
    // 'updated' = vídeo trocado | 'removed' = aula deletada | 'mixed' = ambos.
    kind: { type: String, default: "updated" },
    updatedCount: { type: Number, default: 0 },
    removedCount: { type: Number, default: 0 },
    // Via props (não state): a ação acerta o curso certo mesmo se o slot pendente
    // for sobrescrito por outro aviso enquanto este modal está aberto.
    enrollId: { type: [String, Number], default: null },
    orphanLoIds: { type: Array, default: () => [] },
    redownloadLoIds: { type: Array, default: () => [] },
  },
  computed: {
    isRemoved() {
      return this.kind === "removed";
    },
    isMixed() {
      return this.kind === "mixed";
    },
    icon() {
      return this.isRemoved ? "delete_outline" : "autorenew";
    },
    title() {
      if (this.isMixed) return "Conteúdo do curso mudou";
      if (this.isRemoved) return this.count === 1 ? "Aula removida" : "Aulas removidas";
      return "Conteúdo atualizado";
    },
    verb() {
      return this.isRemoved ? "removeu" : "atualizou";
    },
    headline() {
      // só usado no caso misto (≥2 aulas, sempre plural)
      return `O produtor atualizou ${this.updatedCount} e removeu ${this.removedCount} aulas deste curso.`;
    },
    removalLine() {
      if (this.isRemoved) {
        return this.count === 1
          ? "A versão baixada foi removida do dispositivo."
          : "As versões baixadas foram removidas do dispositivo.";
      }
      if (this.isMixed) return "As versões baixadas foram removidas do dispositivo.";
      return this.count === 1
        ? "A versão baixada da aula foi removida automaticamente."
        : "As versões baixadas das aulas foram removidas automaticamente.";
    },
  },
  methods: {
    ack(redownload) {
      this.$store.dispatch("download/acknowledgeOutdatedNotice", {
        enrollId: this.enrollId,
        orphanLoIds: this.orphanLoIds,
        redownloadLoIds: this.redownloadLoIds,
        redownload,
      });
      this.$emit("close");
    },
    redownload() {
      this.ack(true);
    },
    dismiss() {
      this.ack(false);
    },
  },
  mounted() {
    // Trava o scroll do documento atrás do modal (overlay do vue-js-modal não trava
    // em WebView). Regra .oln-no-scroll no style.scss.
    document.documentElement.classList.add("oln-no-scroll");
    document.body.classList.add("oln-no-scroll");
  },
  beforeDestroy() {
    document.documentElement.classList.remove("oln-no-scroll");
    document.body.classList.remove("oln-no-scroll");
  },
};
</script>

<style scoped lang="scss">
.oln {
  padding: 28px 24px 24px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &--light {
    background: #ffffff;
    color: #1a1a2e;

    .oln__title {
      color: #1a1a2e;
    }
    .oln__course-name {
      color: #555;
    }
    .oln__desc {
      color: #666;
    }
    .oln__lessons {
      color: #888;
    }
  }

  &--dark {
    background: #16161f;
    color: #f0f0f0;

    .oln__title {
      color: #f0f0f0;
    }
    .oln__course-name {
      color: #a0a0b0;
    }
    .oln__desc {
      color: #b0b0c0;
    }
    .oln__lessons {
      color: #8a8a9a;
    }
  }
}

.oln__icon-wrap {
  margin-bottom: 20px;
}

.oln__icon-bg {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(37, 99, 235, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.oln__icon {
  font-size: 28px;
  color: #2563eb;
}

.oln--removed .oln__icon-bg {
  background: rgba(245, 158, 11, 0.12);
}
.oln--removed .oln__icon {
  color: #f59e0b;
}

.oln__body {
  margin-bottom: 12px;
}

.oln__title {
  font-family: "Montserrat", Roboto, sans-serif;
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 8px;
  line-height: 1.3;
}

.oln__course-name {
  font-size: 13px;
  font-weight: 500;
  margin: 0 0 12px;
  padding: 4px 12px;
  border-radius: 20px;
  display: inline-block;
  background: rgba(37, 99, 235, 0.08);
  color: #2563eb;
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.oln__desc {
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  padding: 0 4px;
}

.oln__lessons {
  font-size: 12px;
  line-height: 1.5;
  margin: 12px 0 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(128, 128, 128, 0.1);
}

.oln__question {
  font-size: 13px;
  font-weight: 600;
  margin: 18px 0 0;
}

.oln__actions {
  display: flex;
  width: 100%;
  gap: 10px;

  &--single {
    justify-content: center;
  }
}

.oln__btn {
  flex: 1;
  height: 48px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  font-family: "Montserrat", Roboto, sans-serif;
  cursor: pointer;
  border: none;
  outline: none;
  transition: opacity 0.15s ease, background 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:active:not(:disabled) {
    opacity: 0.85;
  }
}

.oln__btn-icon {
  font-size: 18px;
  line-height: 1;
}

.oln__btn--download {
  flex: 1.7;
  background: #2563eb;
  color: #fff;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
}

.oln__btn--ghost {
  flex: 1;
  background: transparent;
  color: #888;
  border: 1px solid rgba(128, 128, 128, 0.35);

  &:hover {
    background: rgba(128, 128, 128, 0.08);
  }
}
</style>
